import { useCountryOptions, CountryCodes } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { Button, Grid, SelectChangeEvent } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { languages } from '@e3dc-react/shell/Configuration/languages'
import { SERVICE_LEVELS, INSTALLER_PARTNER } from 'src/Configuration'
import SeparatorLine from 'src/Elements/Separator/SeparatorLine'
import { CompanySearch, ConditionalTooltipCheckbox, FormCheckbox, SearchableSelect, Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { UserForm, useFetchCompany, useFetchCompanyLocation, CompanyOption, useQntmInviteDialog } from 'src/Libs/service-dashboard'
import { UserLevel, userLevels } from 'src/State/User/UserModel'
import useModals from 'src/Hooks/useModals'
import { getInitialLanguageByCountry } from 'src/Libs/languages/functions/default'
import { useTypedSelector } from 'src/State/RootReducer'

const useStyles = makeStyles()(theme => ({
  form: {
    marginTop: theme.spacing(2),
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    width: '100%',
  },
  outlinedButtons: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}))

interface UserLevelOption {
  value: UserLevel
  label: string
}

const allowedUserLevels = ['USER', ...INSTALLER_PARTNER, 'E3DC_SERVICE'] as UserLevel[]

export const allowedUserLevelOptions = allowedUserLevels.map(level => ({
  value: level,
  label: level,
}))

interface UserViewFormProps {
  initialValues: UserForm
  isEditing?: boolean
  deleteUser: (id: number) => void
  resendInvitationEmail: (id: number, sendIpin?: boolean) => void
  resendIpinEmail: (id: number) => void
  onSubmit: (values: UserForm) => Promise<void>
}

/**
 * Component that holds the form to edit user details
 */
const UserViewForm: React.FC<UserViewFormProps> = ({ initialValues, isEditing, deleteUser, resendInvitationEmail, resendIpinEmail, onSubmit }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { control, getValues, handleSubmit, setValue } = useFormContext<UserForm>()
  const { allCountries } = useCountryOptions()
  const { id } = useParams<{ id: string }>()
  const { openConfirmationModal } = useModals()
  const watchedCompanyId = useWatch({ control, name: 'company' })
  const watchedUserLevel = useWatch({ control, name: 'userLevel' })
  const watchedUseCompanyAddress = useWatch({ control, name: 'useCompanyAddress' })
  const watchedIpin = useWatch({ control, name: 'ipin' })
  const watchedQntmCertification = useWatch({ control, name: 'qntmCertified' })
  const companyId = watchedCompanyId ?? initialValues.company
  const userLevel = watchedUserLevel ?? initialValues.userLevel
  const useCompanyAddress = watchedUseCompanyAddress ?? initialValues.useCompanyAddress ?? false
  const userId = useTypedSelector(state => state.auth.userId)
  const navigate = useNavigate()

  const [userCompany] = useFetchCompany(companyId)
  const [userCompanyLocation] = useFetchCompanyLocation(companyId)
  const isActiveCompanyShowPermission = userCompany?.shopPermission ?? false

  const showInstallerPartnerFields = INSTALLER_PARTNER.includes(userLevel)
  const showServiceUserFields = SERVICE_LEVELS.includes(userLevel)
  const isInstaller = userLevel === 'INSTALLER'

  const openQntmInviteDialog = useQntmInviteDialog()

  const userLevelOptionsToDisplay = useMemo(
    () =>
      userLevels.reduce((finalRolesArray, role) => {
        /**
         * If user level is not initally selected as value that is not in allowed levels list(e.g Admin, Root etc.), the option should not be displayed
         */
        if (!allowedUserLevels.includes(role) && initialValues.userLevel !== role) {
          return finalRolesArray
        }

        return [...finalRolesArray, { value: role, label: t(`framework.userBadge.userlevel.${role}`) }]
      }, [] as UserLevelOption[]),
    [initialValues.userLevel, t]
  )

  const languageOptions = languages.map(option => ({
    value: option.shortName,
    label: t(`common.languages.${option.translationKey}`),
  }))

  const onChangeUserLevel = (event: SelectChangeEvent<unknown>): void => {
    const nextUserLevel = event.target.value as UserLevel
    const shouldKeepCompanyFields = INSTALLER_PARTNER.includes(nextUserLevel)

    /** Some fields that depend on user level should be reset */
    setValue('ipin', '', { shouldDirty: true, shouldValidate: true })
    setValue('qntmCertified', false, { shouldDirty: true, shouldValidate: true })
    setValue('infinityCertified', false, { shouldDirty: true, shouldValidate: true })
    setValue('useShop', false, { shouldDirty: true, shouldValidate: true })
    setValue('couponPermission', false, { shouldDirty: true, shouldValidate: true })
    setValue('useCompanyAddress', shouldKeepCompanyFields ? getValues('useCompanyAddress') ?? false : false, {
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('company', shouldKeepCompanyFields ? getValues('company') : undefined, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const onChangeCompany = (value: CompanyOption | null): void => {
    setValue('company', value?.value, { shouldDirty: true, shouldValidate: true })

    /** Some fields that depend on company should be reset */
    setValue('useShop', false, { shouldDirty: true, shouldValidate: true })
    if (isInstaller && isEditing && value?.value !== initialValues.company) {
      setValue('ipin', '', { shouldDirty: true, shouldValidate: true })
    }
  }

  const selectDefaultLanguage = (value: string | number | boolean): void => {
    setValue('language', getInitialLanguageByCountry(value as string), { shouldDirty: true, shouldValidate: true })
  }

  const setUserAddressFromCompany = useCallback(() => {
    if (!userCompanyLocation) return

    setValue('city', userCompanyLocation?.Address ?? '', { shouldDirty: true, shouldValidate: true })
    setValue('country', userCompanyLocation?.Land ?? CountryCodes.GERMANY, { shouldDirty: true, shouldValidate: true })
    setValue('zipCode', userCompanyLocation?.PLZ ?? '', { shouldDirty: true, shouldValidate: true })
    setValue('address', userCompanyLocation?.PlaceOR ?? '', { shouldDirty: true, shouldValidate: true })
  }, [setValue, userCompanyLocation])

  const onConfirmInvitationResend = (sendIpin: boolean): void => {
    if (id) resendInvitationEmail(+id, sendIpin)
  }

  useEffect(() => {
    if (useCompanyAddress) {
      setUserAddressFromCompany()
    }
  }, [setUserAddressFromCompany, useCompanyAddress])

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container>
        <Grid item xs={12} md={6}>
          <Grid container>
            <Grid item xs={12} marginBottom={2}>
              <TextField label={t('common.login.user')} name={'username'} required fullWidth />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <TextField label={t('common.firstName')} name={'firstName'} required fullWidth />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <TextField label={t('common.lastName')} name={'lastName'} required fullWidth />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <TextField label={t('common.email')} name={'eMail'} required fullWidth />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <Select
                label={t('common.userLevel')}
                onChange={onChangeUserLevel}
                name={'userLevel'}
                options={userLevelOptionsToDisplay}
                required
                fullWidth
              />
            </Grid>
            {showInstallerPartnerFields && (
              <Grid item xs={12} marginBottom={2}>
                <CompanySearch
                  name={'company'}
                  maxDisplayedResultCount={10}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onValueChange={onChangeCompany}
                  initialSearchValue={userCompany?.name}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Grid container>
            <Grid item xs={12} marginBottom={2}>
              <TextField label={t('common.address')} name={'address'} disabled={useCompanyAddress} fullWidth />
            </Grid>
            <Grid item xs={3} marginBottom={2}>
              <TextField label={t('common.zipCode')} name={'zipCode'} disabled={useCompanyAddress} fullWidth />
            </Grid>
            <Grid item xs={9} marginBottom={2}>
              <TextField label={t('common.city')} name={'city'} disabled={useCompanyAddress} fullWidth />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <SearchableSelect
                label={t('common.country')}
                onChange={selectDefaultLanguage}
                name={'country'}
                options={allCountries}
                disabled={useCompanyAddress}
              />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <TextField label={t('common.phone')} name={'phoneNumber'} fullWidth />
            </Grid>
            <Grid item xs={12} marginBottom={2}>
              <Select label={t('common.language')} name={'language'} options={languageOptions} fullWidth />
            </Grid>
            {isInstaller && (
              <Grid item xs={12} marginBottom={2}>
                <TextField label={t('common.ipin')} name={'ipin'} required fullWidth />
              </Grid>
            )}
          </Grid>
        </Grid>
        {showInstallerPartnerFields && (
          <>
            <Grid item xs={12}>
              <FormCheckbox name={'useCompanyAddress'} label={t('user.userData.copy-company-data-checkbox-title')} />
            </Grid>
            <Grid item xs={12}>
              <ConditionalTooltipCheckbox
                name={'useShop'}
                label={t('user.shopPermission.title')}
                disabled={!isActiveCompanyShowPermission}
                disableReason={t('service-dashboard.company.shopReasonDisable')}
              />
            </Grid>
          </>
        )}
        {showServiceUserFields && (
          <Grid item xs={12}>
            <FormCheckbox name={'couponPermission'} label={t('user.couponPermission.title')} />
          </Grid>
        )}

        {isInstaller && (
          <>
            <Grid item xs={12}>
              <FormCheckbox name={'infinityCertified'} label={t('common.infinityCertified')} />
            </Grid>
            <Grid item xs={12}>
              <FormCheckbox name={'qntmCertified'} label={t('common.qntmCertified')} />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormCheckbox name={'eMailNotification'} label={t('administration.users.emailNotification')} />
        </Grid>
      </Grid>
      <SeparatorLine />
      <div className={classes.buttonRow}>
        <div className={classes.outlinedButtons}>
          {/** TODO: Implement simulating users */}
          {/* <Button type="button" variant="outlined" size="large" color="primary" disabled>
            <K1View className={classes.viewIcon} color={'disabled'} />
            {t('common.login.resetPassword')}
          </Button> */}
          {/** TODO: Implement simulating users */}
          {/* <Button type="button" variant="outlined" size="large" color="primary" disabled>
            <K1View className={classes.viewIcon} color={'disabled'} />
            {t('user.simulateUser')}
          </Button> */}
          {isEditing && (
            <>
              <Button
                type="button"
                variant="outlined"
                size="large"
                color="primary"
                onClick={() => {
                  openConfirmationModal({
                    buttonText: [t('common.cancel'), t('common.delete')],
                    onConfirm: onClose => {
                      if (id) deleteUser(+id)
                      if (Number.parseInt(id || '') === userId) navigate('/logout')
                      onClose()
                    },
                    text: t('administration.users.removeUserText'),
                    title: t('administration.users.removeUserTitle'),
                  })
                }}
              >
                {t('user.deleteUser')}
              </Button>
              {isInstaller && (
                <>
                  <Button
                    type="button"
                    variant="outlined"
                    size="large"
                    color="primary"
                    onClick={() => {
                      openConfirmationModal({
                        buttonText: [t('user.installer.resend-ipin.button-yes'), t('user.installer.resend-ipin.button-no')],
                        onConfirm: onClose => {
                          onClose()
                        },
                        onCancel: onClose => {
                          if (id) {
                            resendIpinEmail(+id)
                          }
                          onClose()
                        },
                        text:
                          watchedQntmCertification && watchedIpin?.endsWith('50')
                            ? t('user.installer.resend-ipin.confirm.qntm-installer-text')
                            : t('user.installer.resend-ipin.confirm.text'),
                        title: t('user.installer.resend-ipin.confirm.title'),
                      })
                    }}
                  >
                    {t('user.installer.resend-ipin.button-title')}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    size="large"
                    color="primary"
                    onClick={() => {
                      if (watchedQntmCertification && watchedIpin?.endsWith('50')) {
                        openQntmInviteDialog({
                          hasQntmIpin: watchedIpin?.endsWith('50') ?? false,
                          onSave: () => onConfirmInvitationResend(true),
                          onCancel: () => onConfirmInvitationResend(false),
                        })
                      } else {
                        openConfirmationModal({
                          buttonText: [t('common.cancel'), t('common.confirm')],
                          onConfirm: () => {
                            onConfirmInvitationResend(true)
                          },
                          text: t('user.installer.resend-invitation.confirm.text'),
                          title: t('user.installer.resend-invitation.confirm.title'),
                        })
                      }
                    }}
                  >
                    {t('user.installer.resend-invitation.button-title')}
                  </Button>
                </>
              )}
            </>
          )}
        </div>

        <Button type="submit" variant="contained" size="large" color="primary">
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export default UserViewForm
